import React from "react";
import BillableHoursCard from "./BillableHoursCard";
import GridCard from "./GridCard";
import { useBillableHours } from "./hooks/useBillableHours";
import { useHoursCompleted } from "./hooks/useHoursCompleted";
import { useLeaveOfAbsence } from "./hooks/useLeaveOfAbsence";
import { useAuthorizationByRole } from "./hooks/useAuthorizationByRole";
import LeaveOfAbsenceCard from "./LeaveOfAbsenceCard";
import AuthorizationCard from "./AuthorizationCard";
import CardSection from "./CardSection";

interface Props {
  username: string;
  year: string;
  quarter: number;
  status?: string;
  display: {
    BillableGraph: boolean;
    BillableTable: boolean;
    WeeklyHours: boolean;
    OtherHours: boolean;
    LOA: boolean;
    RAS: boolean;
  };
}

const BillableRequirementsSection: React.FC<Props> = ({
  username,
  year,
  quarter,
  status,
  display,
}) => {
  const graphEnabled = !!username && !!year && display.BillableGraph;

  const { data: billableHoursData, loading: billableLoading } =
    useBillableHours(username, year, quarter, graphEnabled);

  const hoursEnabled =
    !!username &&
    !!year &&
    (display.BillableTable || display.WeeklyHours || display.OtherHours);

  const { hoursProgress, weeklyHours, usedHours, isLeadership, loading: hoursLoading } =
    useHoursCompleted(username, year, quarter, status, hoursEnabled);

  const loaEnabled = !!username && !!year && display.LOA;
  const rasEnabled = !!username && !!year && display.RAS;

  const { data: loaData, loading: loaLoading } =
    useLeaveOfAbsence(username, year, quarter, loaEnabled);

  const { data: rasData, loading: rasLoading } =
    useAuthorizationByRole(username, year, quarter, rasEnabled);

  // Roles flagged Leadership on the PBS Roles List only accrue billed + billable credit
  // toward their quarterly total, so the other columns are hidden rather than shown as
  // unused numbers. The flag comes from the API, not a list of role names.
  const leadership = isLeadership === true;

  const usedHoursHeaders = leadership
    ? ["Billable Credit"]
    : ["Meeting Hours", "Holiday Hours", "PTO/Sick/FH Hours", "Billable Credit"];

  const usedHoursRows = usedHours
    ? [leadership ? [usedHours[3]] : usedHours]
    : [];

  return (
    <CardSection title="Billable Requirements">
        {/* LEFT SIDE */}
        <div className="col-span-12 lg:col-span-8">
          {display.BillableGraph && (
            <BillableHoursCard
              title="Billable Hours per Week"
              data={billableHoursData}
              loading={billableLoading}
              description="Weekly billable performance"
            />
          )}

          {display.LOA && <LeaveOfAbsenceCard data={loaData} loading={loaLoading} />}

          {display.RAS && <AuthorizationCard data={rasData} loading={rasLoading} />}
        </div>

        {/* RIGHT SIDE */}
        <div className="col-span-12 lg:col-span-4">
          {display.BillableTable && (
            <GridCard
              title="Hours Progress"
              headers={[
                "Prorated Required Hours",
                "Total Hours in Quarter",
                "Pace Percentage",
              ]}
              data={hoursProgress ? [hoursProgress] : []}
              loading={hoursLoading}
            />
          )}

          {display.WeeklyHours && (
            <GridCard
              title="Current Weekly Average"
              headers={["Weekly Required Hours", "Weekly Average Billed"]}
              data={weeklyHours ? [weeklyHours] : []}
              loading={hoursLoading}
            />
          )}

          {display.OtherHours && (
            <GridCard
              title="Used Hours to Date"
              headers={usedHoursHeaders}
              data={usedHoursRows}
              loading={hoursLoading}
            />
          )}
        </div>
    </CardSection>
  );
};

export default BillableRequirementsSection;